/**
 * Contact URL Builder
 *
 * Builds personalized contact page URLs with tracking parameters.
 * Used across the site to track where visitors came from.
 */

export type ContactSource = 'tool' | 'blog' | 'kb' | 'service' | 'industry';

export interface ContactUrlParams {
  /** Source page type */
  src: ContactSource;
  /** Page slug (e.g., 'secure-password-generator') */
  id: string;
  /** Content category (e.g., 'security', 'compliance') */
  cat?: string;
}

/**
 * Build a personalized contact URL with tracking parameters
 *
 * @example
 * buildContactUrl({ src: 'tool', id: 'secure-password-generator', cat: 'security' })
 * // Returns: '/contact?src=tool&id=secure-password-generator&cat=security'
 *
 * @example
 * buildContactUrl({ src: 'service', id: 'vciso' })
 * // Returns: '/contact?src=service&id=vciso'
 */
export function buildContactUrl(params: ContactUrlParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('src', params.src);
  searchParams.set('id', params.id);
  if (params.cat) {
    searchParams.set('cat', params.cat);
  }
  return `/contact?${searchParams.toString()}`;
}

/**
 * Parse contact URL search params into typed object
 * Returns null if params are missing or invalid
 *
 * Supports two formats:
 * 1. New format: ?src=service&id=vciso&cat=security
 * 2. Legacy format: ?ref=service-vciso (extracts src and id from ref)
 */
export function parseContactParams(searchParams: URLSearchParams): ContactUrlParams | null {
  const validSources: ContactSource[] = ['tool', 'blog', 'kb', 'service', 'industry'];

  // Try new format first (src/id params)
  const src = searchParams.get('src');
  const id = searchParams.get('id');

  if (src && id && validSources.includes(src as ContactSource)) {
    const cat = searchParams.get('cat');
    return {
      src: src as ContactSource,
      id,
      ...(cat ? { cat } : {}),
    };
  }

  // Try legacy format (ref=service-slug or ref=industry-slug)
  const ref = searchParams.get('ref');
  if (ref) {
    // Parse ref like "service-penetration-testing" or "industry-healthcare"
    for (const source of validSources) {
      if (ref.startsWith(`${source}-`)) {
        const extractedId = ref.slice(source.length + 1); // Remove "service-" prefix
        if (extractedId) {
          return {
            src: source,
            id: extractedId,
          };
        }
      }
    }
  }

  return null;
}
